import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Compass,
  ShoppingCart,
  Hospital,
  Fuel,
  Landmark,
  GraduationCap,
  UtensilsCrossed,
  Car,
  Camera,
  Bot,
  ChevronDown,
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { resolveAreaIntelligence, resolveModuleDisplayStatus } from '../../lib/areaIntelligence/config';
import { AMENITY_CATEGORIES, formatKm } from '../../lib/areaIntelligence/googleClient';
import { useAreaIntelligence } from '../../lib/areaIntelligence/useAreaIntelligence';

export const AREA_INTELLIGENCE_MODULE_TEST_ID = 'area-intelligence-module';

const CATEGORY_ICONS = {
  grocery: ShoppingCart,
  hospital: Hospital,
  gas: Fuel,
  bank: Landmark,
  school: GraduationCap,
  food: UtensilsCrossed,
};

/**
 * Premium embeddable Area Intelligence module — visible Google-powered
 * neighborhood facts for a coordinate: nearest amenities, base commute,
 * Street View peek, and a JC handoff.
 *
 * Honesty rules: names/distances come from live Google responses or real
 * coordinate math — never invented. A failed category renders an explicit
 * unavailable state. Without a key the module renders nothing (platform
 * surfaces already cover curated content).
 */
export default function AreaIntelligenceModule({ origin, baseTargets = [], distanceUnit = 'km', className = '' }) {
  const { t } = useLanguage();
  const [compareAll, setCompareAll] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const { status, amenities, commutes, streetViewUrl, mode } = useAreaIntelligence(origin, baseTargets, { compareAll });
  const [streetViewFailed, setStreetViewFailed] = useState(false);

  const hasCoords = Boolean(origin && Number.isFinite(origin.lat) && Number.isFinite(origin.lng));
  const display = resolveModuleDisplayStatus({
    enabled: resolveAreaIntelligence().enabled,
    hasCoords,
    mode,
    status,
  });
  if (display === 'hidden') return null;

  const CHIPS = {
    live: { label: t('area_intel.status_live', 'Live · Google'), cls: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
    loading: { label: t('area_intel.status_loading', 'Scanning area…'), cls: 'bg-champagne-100 text-slate-600 border-champagne-300' },
    unavailable: { label: t('area_intel.status_unavailable', 'Temporarily unavailable'), cls: 'bg-slate-100 text-slate-500 border-slate-300' },
    no_coords: { label: t('area_intel.status_no_coords', 'Coordinates needed'), cls: 'bg-amber-50 text-amber-700 border-amber-300' },
  };
  const statusChip = CHIPS[display];

  return (
    <section
      className={`glass-card overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-500 ${className}`}
      data-testid={AREA_INTELLIGENCE_MODULE_TEST_ID}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 bg-gradient-to-r from-champagne-100 to-ivory border-b border-champagne-200">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-signal-blue to-military-gold flex items-center justify-center">
            <Compass className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wider text-military-gold font-semibold">
              {t('area_intel.title', 'Area Intelligence')}
            </p>
            <p className="text-xs text-slate-500">
              {t('area_intel.module_subtitle', 'Real distances around this home')}
            </p>
          </div>
        </div>
        <span className={`text-[11px] px-2.5 py-1 rounded-full border font-medium ${statusChip.cls}`}>
          {statusChip.label}
        </span>
      </div>

      <div className="p-5 space-y-4">
        {display === 'no_coords' && (
          <p className="text-sm text-slate-600" data-testid="area-intel-no-coords">
            {t(
              'area_intel.no_coords_note',
              'Area coordinates for this home have not been set yet, so live distances cannot be calculated. The neighborhood details elsewhere on this page still apply.',
            )}
          </p>
        )}
        {display !== 'no_coords' && (
        <>
        {/* Base commute cards */}
        {commutes.length > 0 && (
          <div className="space-y-2">
            {commutes.map((commute) => (
              <div
                key={commute.base}
                className="flex items-center justify-between bg-gradient-to-r from-slate-900 to-slate-800 text-white rounded-xl px-4 py-3 shadow-md"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <Car className="w-4 h-4 text-amber-400 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{commute.base}</p>
                    <p className="text-[11px] text-slate-300">
                      {t('area_intel.drive_to_base', 'Drive to base')}
                    </p>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-lg font-bold text-amber-400">{commute.durationText}</p>
                  {Number.isFinite(commute.distanceKm) && (
                    <p className="text-[11px] text-slate-300">{formatKm(commute.distanceKm, distanceUnit)}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Amenity grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          {AMENITY_CATEGORIES.map((cat) => {
            const Icon = CATEGORY_ICONS[cat.key] || Compass;
            const results = amenities[cat.key];
            const nearest = Array.isArray(results) ? results[0] : null;
            const isOpen = expanded === cat.key;
            return (
              <div
                key={cat.key}
                className="bg-white border border-champagne-200 rounded-xl px-3.5 py-3 transition-all hover:border-military-gold/40 hover:shadow-sm"
              >
                <button
                  className="w-full flex items-start gap-3 text-left"
                  onClick={() => setExpanded(isOpen ? null : cat.key)}
                  data-testid={`area-intel-cat-${cat.key}`}
                >
                  <div className="w-8 h-8 rounded-lg bg-champagne-100 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-4 h-4 text-military-gold" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">
                        {t(cat.labelKey, cat.defaultLabel)}
                      </p>
                      {Array.isArray(results) && results.length > 1 && (
                        <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                      )}
                    </div>
                    {status === 'loading' ? (
                      <div className="h-4 mt-1 w-24 bg-champagne-100 rounded animate-pulse" />
                    ) : nearest ? (
                      <>
                        <p className="text-sm font-medium text-slate-800 truncate">{nearest.name}</p>
                        {Number.isFinite(nearest.distanceKm) && (
                          <p className="text-xs text-slate-500">{formatKm(nearest.distanceKm, distanceUnit)}</p>
                        )}
                      </>
                    ) : Array.isArray(results) ? (
                      <p className="text-xs text-slate-400 mt-1">
                        {t('area_intel.none_nearby', 'None found within 8 km')}
                      </p>
                    ) : (
                      <p className="text-xs text-slate-400 mt-1">
                        {t('area_intel.cat_unavailable', 'Unavailable right now')}
                      </p>
                    )}
                  </div>
                </button>
                {isOpen && Array.isArray(results) && results.length > 1 && (
                  <ul className="mt-2 pl-11 space-y-1 animate-in fade-in duration-300">
                    {results.slice(1).map((place) => (
                      <li key={place.id} className="text-xs text-slate-600 flex items-center justify-between gap-2">
                        <span className="truncate">{place.name}</span>
                        <span className="text-slate-400 flex-shrink-0">{formatKm(place.distanceKm, distanceUnit)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>

        {/* Street View peek */}
        {streetViewUrl && !streetViewFailed && (
          <div>
            <div className="rounded-xl overflow-hidden border border-champagne-200">
              <img
                src={streetViewUrl}
                alt={t('area_intel.street_view_label', 'Street View')}
                className="w-full h-36 object-cover"
                loading="lazy"
                onError={() => setStreetViewFailed(true)}
              />
            </div>
            <p className="mt-1 text-[11px] text-slate-500 flex items-center gap-1">
              <Camera className="w-3 h-3" />
              {t('area_intel.street_view_label', 'Street View')}
            </p>
          </div>
        )}

        {/* Actions — all real */}
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <Link
            to="/concierge"
            className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-signal-blue hover:bg-signal-blue-hover text-white text-xs font-medium rounded-full transition-colors"
            data-testid="area-intel-ask-jc"
          >
            <Bot className="w-3.5 h-3.5" />
            {t('area_intel.ask_jc', 'Ask JC about this area')}
          </Link>
          {baseTargets.length > 1 && !compareAll && status === 'live' && (
            <button
              onClick={() => setCompareAll(true)}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-champagne-100 hover:bg-champagne-200 border border-champagne-200 text-slate-700 text-xs font-medium rounded-full transition-colors"
              data-testid="area-intel-compare-commute"
            >
              <Car className="w-3.5 h-3.5 text-military-gold" />
              {t('area_intel.compare_commute', 'Compare commute')}
            </button>
          )}
        </div>

        </>
        )}

        {display === 'unavailable' && (
          <p className="text-[11px] text-slate-500">
            {t(
              'area_intel.unavailable_note',
              'Live area data is temporarily unavailable. Distances shown elsewhere on this page come from our own records.',
            )}
          </p>
        )}
      </div>
    </section>
  );
}
